import React, { useState, useEffect } from 'react';
import { useStripeContext } from '../context/StripeContext';
import { useAuth } from '../contexts/AuthContext';
import { CheckIcon } from '@heroicons/react/20/solid'
import { Link, useNavigate } from 'react-router-dom';

const tiers = [
  {
    name: 'Free',
    id: 'tier-hobby',
    href: '/Slideshow',
    price: '$0',
    description: "Perfect for small slideshows and just getting started.",
    features: [
      'Up to 50 images per folder', 
      '6 second transition'
    ],
    featured: false,
  },
  {
    name: 'Showtime',
    id: 'tier-enterprise',
    href: '/Slideshow',
    price: 'A$2.50',
    description: 'Scale up your slideshows with more features.',
    features: [
      'Up to 1000 images per folder',
      'Customisable transition',
      'One-time payment (Valid for 7 days)'
    ],
    featured: true,
  },
  {
    name: 'Showtime Plus',
    id: 'tier-enterprise-plus',
    href: '/Slideshow',
    price: 'A$4.00',
    description: 'Get the most out of your slideshows with premium features.',
    features: [
      'Up to 2500 images per folder',
      'Customisable transition',
      'One-time payment (Valid for 30 days)'
    ],
    featured: false,
  },
]

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

const Pricing = () => {
  const { createCheckoutSession, paymentStatus, error: stripeError, daysRemaining } = useStripeContext();
  const { user } = useAuth();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (stripeError) {
      setError(stripeError);
    }
  }, [stripeError]);

  const handlePurchase = async (priceId) => {
    try {
      setError(null);
      setIsLoading(true);
      
      if (!user) {
        navigate('/register');
        return;
      }
      
      await createCheckoutSession(priceId);
    } catch (error) {
      console.error('Error initiating purchase:', error);
      setError(error.message || 'An error occurred while processing your purchase. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (paymentStatus === 'paid' && user) {
    return (
      <div className="relative isolate bg-white min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="absolute inset-0 bg-grid-gray-900/[0.04] bg-[length:20px_20px]" />
        <div className="relative px-6 py-24 sm:py-32 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="text-base font-semibold leading-7 text-primary">Your Subscription</h2>
            <p className="mt-2 text-5xl font-semibold tracking-tight text-balance text-gray-900 sm:text-6xl">
              Active Showtime Plan
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              You currently have an active Snappy Slides subscription.
              {daysRemaining !== null && (
                <span className="block mt-4 text-primary font-semibold">
                  {daysRemaining > 0 
                    ? `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} remaining in your subscription`
                    : 'Your subscription has expired'}
                </span>
              )}
            </p>
            {daysRemaining !== null && daysRemaining <= 0 && (
              <button
                onClick={() => handlePurchase('price_1RKCEyCABAGDSFEqKSOoAF3a')}
                disabled={isLoading}
                className="mt-8 inline-flex items-center justify-center rounded-md bg-primary px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                {isLoading ? 'Processing...' : 'Renew Subscription'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative isolate bg-white min-h-[calc(100vh-4rem)]">
      <div className="absolute inset-0 bg-grid-gray-900/[0.04] bg-[length:20px_20px]" />
      <div className="relative px-6 py-24 sm:py-32 lg:px-8">
        <div aria-hidden="true" className="absolute inset-x-0 -top-3 -z-10 transform-gpu overflow-hidden px-36 blur-3xl">
          <div
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
            className="mx-auto aspect-1155/678 w-[72.1875rem] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-10"
          />
        </div>
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-base font-semibold leading-7 text-primary">Pricing</h2>
          <p className="mt-2 text-5xl font-semibold tracking-tight text-balance text-gray-900 sm:text-6xl">
            Choose the right plan for you
          </p>
        </div>
        <p className="mx-auto mt-6 max-w-2xl text-center text-lg leading-8 text-gray-600">
          {user ? 'Upgrade to Showtime or Showtime Plus to access more features.' : 'Try our free plan now or create an account to unlock premium features.'}
        </p>
        <div className="mx-auto mt-16 grid max-w-lg grid-cols-1 items-center gap-y-6 sm:mt-20 sm:gap-y-0 lg:max-w-4xl lg:grid-cols-3">
          {tiers.map((tier, tierIdx) => (
            <div
              key={tier.id}
              className={classNames(
                tier.featured ? 'relative bg-[#F8AD9D] shadow-2xl' : 
                tier.name === 'Showtime Plus' ? 'relative bg-[#F08080] shadow-2xl text-white' : 
                'bg-white sm:mx-8 lg:mx-0',
                tier.featured || tier.name === 'Showtime Plus'
                  ? ''
                  : tierIdx === 0
                    ? 'rounded-3xl'
                    : tierIdx === tiers.length - 1
                      ? 'rounded-3xl'
                      : '',
                'p-8 ring-1 ring-gray-200 sm:p-10',
              )}
            >
              <h3
                id={tier.id}
                className={classNames(
                  tier.featured || tier.name === 'Showtime Plus' ? 'text-white' : 'text-primary',
                  'text-base font-semibold leading-7'
                )}
              >
                {tier.name}
              </h3>
              <p className="mt-4 flex items-baseline gap-x-2">
                <span
                  className={classNames(
                    tier.featured || tier.name === 'Showtime Plus' ? 'text-white' : 'text-gray-900',
                    'text-5xl font-semibold tracking-tight',
                  )}
                >
                  {tier.price}
                </span>
              </p>
              <p className={classNames(
                tier.featured || tier.name === 'Showtime Plus' ? 'text-white/90' : 'text-gray-600',
                'mt-6 text-base leading-7'
              )}>
                {tier.description}
              </p>
              <ul
                className={classNames(
                  tier.featured || tier.name === 'Showtime Plus' ? 'text-white/90' : 'text-gray-600',
                  'mt-8 space-y-3 text-sm leading-6 sm:mt-10',
                )}
              >
                {tier.features.map((feature) => (
                  <li key={feature} className="flex gap-x-3">
                    <CheckIcon
                      aria-hidden="true"
                      className={classNames(
                        tier.featured || tier.name === 'Showtime Plus' ? 'text-white' : 'text-primary',
                        'h-6 w-5 flex-none'
                      )}
                    />
                    {feature}
                  </li>
                ))}
              </ul>
              {tier.featured ? (
                <button
                  onClick={() => handlePurchase('price_1RLCHDCABAGDSFEqWmVkyK36')}
                  disabled={isLoading}
                  className={classNames(
                    'bg-white text-[#F8AD9D] hover:bg-gray-50 focus-visible:outline-white',
                    'mt-8 block rounded-md px-3.5 py-2.5 text-center text-sm font-semibold leading-6 focus-visible:outline-2 focus-visible:outline-offset-2 sm:mt-10',
                    'w-full',
                    isLoading ? 'opacity-50 cursor-not-allowed' : ''
                  )}
                >
                  {isLoading ? 'Processing...' : 
                   user ? (tier.price === '$0' ? 'Get Started' : 'Buy Now') : 
                   'Create account'}
                </button>
              ) : (
                tier.name === 'Showtime Plus' ? (
                  <button
                    onClick={() => handlePurchase('price_1RLCHWCABAGDSFEqdXHbDiM1')}
                    disabled={isLoading}
                    className={classNames(
                      'bg-white text-[#F08080] hover:bg-gray-50 focus-visible:outline-white',
                      'mt-8 block rounded-md px-3.5 py-2.5 text-center text-sm font-semibold leading-6 focus-visible:outline-2 focus-visible:outline-offset-2 sm:mt-10',
                      'w-full',
                      isLoading ? 'opacity-50 cursor-not-allowed' : ''
                    )}
                  >
                    {isLoading ? 'Processing...' : 
                     user ? (tier.price === '$0' ? 'Get Started' : 'Buy Now') : 
                     'Create account'}
                  </button>
                ) : (
                  <Link
                    to="/slideshow"
                    className={classNames(
                      'bg-white text-[#F08080] border-2 border-[#F08080] hover:bg-[#FBC4AB] hover:border-[#F8AD9D] hover:text-white focus-visible:outline-[#F08080]',
                      'mt-8 block rounded-md px-3.5 py-2.5 text-center text-sm font-semibold leading-6 focus-visible:outline-2 focus-visible:outline-offset-2 sm:mt-10',
                      'w-full'
                    )}
                  >
                    Create Slideshow
                  </Link>
                )
              )}
            </div>
          ))}
        </div>
        {error && (
          <div className="mt-4 text-sm text-red-600 text-center max-w-2xl mx-auto">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default Pricing; 